/**
 * WHICH LINES ON A DINE-IN BILL ARE THE ROUND THAT WAS JUST ADDED.
 *
 * ## The defect this answers
 *
 * A table accumulates ONE bill across the evening. The storefront's cart holds
 * only the new round (`store/thunks/dinein.ts` adopts the order's `_id` and
 * nothing else) and `PUT /web/orders/:id` merges that round into the bill's
 * existing categories, re-deriving every figure. That is right, and it is why
 * the checkout correctly shows one round.
 *
 * The CONFIRMATION does not. `router.push('/orders/:id')` fetches the bill,
 * which is now every round the table has had, and the receipt printed the whole
 * thing under the heading **"+ New Items"** — so an operator who added one
 * Karahi to a table with three earlier rounds was shown eleven lines, all of
 * them labelled new. That is the reported "new item shows with all".
 *
 * ## Why a client-side note and not a server field
 *
 * The server DOES know: `PUT /web/orders/:id` writes an `iterations` history,
 * one snapshot per round. But `toWebOrder` does not project `iterations`, and
 * adding it would be an API change on a running service. It is not needed —
 * the resume lookup already hands the client the bill EXACTLY as it stood
 * before this round, items and all:
 *
 *     GET /web/dinein-tables/:tenant/:table?with_order=true
 *       -> { ...table, order: toWebOrder(order) }   // order.items[].lineId
 *
 * So the line ids that existed BEFORE the round are recorded at the moment the
 * bill is adopted, and the confirmation splits the receipt on them: anything
 * whose `lineId` was already there is "Already Ordered", anything else is the
 * round that was just rung in. Server item ids are minted fresh per round
 * (`priceLines`), so the split is exact rather than heuristic.
 *
 * `sessionStorage` and not the Redux store: this outlives `clearCart()` (which
 * runs the instant the order posts, before the confirmation mounts) but must
 * NOT outlive the tab, and it is per-order so a stale note can never be read
 * against a different bill.
 */

const STORAGE_KEY = "eposmatic-web:prior-round-lines";

/** A note older than this is ignored — a shift is long, a round is not. */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

interface PriorLinesNote {
  orderId: string;
  lineIds: string[];
  at: number;
}

const readNote = (): PriorLinesNote | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PriorLinesNote>;
    if (typeof parsed?.orderId !== "string" || !Array.isArray(parsed.lineIds)) {
      return null;
    }
    return {
      orderId: parsed.orderId,
      lineIds: parsed.lineIds.map(String),
      at: Number(parsed.at) || 0,
    };
  } catch {
    // A private-mode browser can throw on `sessionStorage` access. A missing
    // note is not an error — the receipt simply falls back to one group.
    return null;
  }
};

/**
 * Record the bill as it stands BEFORE a round is added to it. Called from the
 * resume lookup, which is the only place the previous state is ever seen.
 */
export const rememberPriorRoundLines = (
  orderId: string,
  lineIds: (string | null | undefined)[],
): void => {
  if (typeof window === "undefined" || !orderId) return;
  try {
    const note: PriorLinesNote = {
      orderId: String(orderId),
      lineIds: lineIds.filter(Boolean).map(String),
      at: Date.now(),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(note));
  } catch {
    // Ignore: the split is an improvement on the receipt, never a requirement.
  }
};

/** Drop the note — a bill released, settled or replaced has no prior round. */
export const forgetPriorRoundLines = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * The line ids that were already on THIS bill before the last round, or `null`
 * when there is no usable note — which is every takeaway, every delivery, every
 * first round at a table, and any bill this tab did not add to.
 */
export const readPriorRoundLines = (orderId?: string | null): string[] | null => {
  if (!orderId) return null;
  const note = readNote();
  if (!note) return null;
  if (note.orderId !== String(orderId)) return null;
  if (note.at > 0 && Date.now() - note.at > MAX_AGE_MS) return null;
  // An empty list is a REAL answer — "the bill had no lines" cannot happen for
  // an open order, so treat it as no note rather than marking everything new.
  return note.lineIds.length > 0 ? note.lineIds : null;
};
